import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  Input,
  ViewChild,
  ElementRef,
  OnDestroy,
  AfterViewChecked,
  HostListener,
} from '@angular/core';
import { TaskDataService } from '../../../shared/services/task-data.service';
import {
  ITreasuryRequestData,
  TreasuryRequestData,
  ITaskRequest,
  TaskRequest,
  IGeneratedTask,
} from '../../../modules/tasks/model/types';
import notify from 'devextreme/ui/notify';
import validationEngine from 'devextreme/ui/validation_engine';
import {
  getNotificationTimeout,
  sendNotificationMessage,
} from '../../../types/resource';
import { LoggingService } from '../../../framework/logging/logging.service';
import { SeverityLevel } from '../../../framework/logging/severity-level.model';
import { lastValueFrom, Observable, BehaviorSubject, Subscription, combineLatest } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { MessageService } from '../../../shared/components/framework/services/message.service';
import DOMPurify from 'dompurify';

import { DxScrollViewComponent } from 'devextreme-angular/ui/scroll-view';
import { DxDataGridComponent } from 'devextreme-angular/ui/data-grid';
import { IPrototypeGenerationRow } from './types/fandc-generator.types';

import { select, Store } from '@ngrx/store';
import { IFandCGeneratorState } from './types/fandc-generator.state';
import {
  selectEntityNameOptions,
  selectFormatOptions,
  selectFrequencyOffsetOptions,
  selectFrequencyOptions,
  selectGeneratedTasks,
  selectLoans,
  selectMaturityDate,
  selectedLoanNumber,
  selectReferenceDate,
  selectReportEntityOptions,
  selectSelectedDefaultTasks,
  selectSelectedEntityName,
  selectSelectedLoanDetail,
  selectSelectedReportEntity,
  selectTemplates,
  selectRootTemplate,
  selectSelectedTemplateType,
  selectCategory,
  selectedFrequency,
  selectFrequencyOffsetValue,
  selectSelectedFormatOption,
  selectExistingTaskIds,
  selectReviewerList,
  selectSelectedReviewerId,
} from './store/fandc-generator.selectors';
import {
  fetchDefaultTasksForTemplate,
  fetchLoanDetail,
  fetchTemplates,
  fetchTemplateTypes,
  generateTasks,
  resetFandCGenerator,
  setLoanNumber,
  setMaturityDate,
  setReferenceDate,
  setFrequency,
  updatePrototypeTasks,
  updateSelectedDefaultTasks,
  fetchFrequencyOptions,
  setFrequencyOffsetValue,
  fetchLoans,
  fetchFormatOptions,
  setFormatOption,
  setReportEntity,
  setEntityName,
  fetchReportEntityOptions,
  updateGeneratedTasks,
  saveGeneratedTasks,
  fetchGeneratedTasksSuccess,
  saveGeneratedTasksSuccess,
  fetchLoansSuccess,
  saveGenerationTask,
  fetchRootTemplate,
  addToSelectedPrototypeTasks,
  removePrototypeTask,
  setSelectedPrototypeTasks,
  saveGenerationMetadata,
  fetchGenerationMetadata,
  fetchGenerationMetadataSuccess,
  saveGenerationTaskSuccess,
  setTaskId,
  setCategory,
  clearEditModeFormSelections,
  updateBatch,
  updateBatchSuccess,
  fetchReviewers,
  setReviewer,
} from './store/fandc-generator.actions';
import {
  selectTemplateTypes,
  selectDefaultTasksForTemplate,
  selectSelectedPrototypeTasks,
  selectLastPrototypeGenerateResult,
} from './store/fandc-generator.selectors';
import { FrequencyOptionsService } from './services/frequencyoptions.service';
import { Actions, ofType } from '@ngrx/effects';
import saveAs from 'file-saver';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { Workbook } from 'exceljs';

@Component({
  standalone: false,
  selector: 'app-fandc-generator',
  templateUrl: './fandc-generator.component.html',
  styleUrls: ['./fandc-generator.component.scss'],
})
export class FandCGeneratorComponent implements OnInit, OnDestroy {
  @ViewChild('formElement') formElement!: ElementRef;
  @ViewChild('scrollView', { static: false }) scrollView: DxScrollViewComponent;
  @ViewChild('generatedTasksElement', { static: false })
  generatedTasksElementDiv: ElementRef;
  @ViewChild('prototypeTasksGrid', { static: false }) prototypeTasksGrid: DxDataGridComponent;

  @Input() taskTemplateName: string;
  @Input() taskGenerationMetadata: any;
  @Output() onAddNewTaskEventEmitter: EventEmitter<any> = new EventEmitter();

  treasuryRequestTypes: any[];
  bankData: any[];
  fandCRequestData: ITreasuryRequestData;
  loanDisplayText: string = '';
  isLoadingLoanData = false;
  isLoadingMetadata = false;
  isSavingGenerationTask = false;
  isGeneratingTasks = false;
  isSavingTasks = false;
  isLoanNumberPopulated = false; // Track if loan number is populated
  isTaskTypesSelected = false; // Track if task types are selected
  shouldScrollToGeneratedTasks = false;

  /**
   * True on desktop/tablet (>= 1024px). Below this breakpoint the F&C data
   * grids are hidden entirely and a vertical-only card view is shown instead,
   * so mobile has no horizontal grid overflow and stays independent of the
   * desktop grid experience.
   */
  get isDesktopView(): boolean {
    return typeof window !== 'undefined' && window.innerWidth >= 1024;
  }

  /**
   * Horizontal grid drag/swipe scrolling is a desktop-only affordance (>= 1024px).
   * It is only meaningful while the grid is visible, which is the same
   * breakpoint as {@link isDesktopView}.
   */
  get gridDragScrollEnabled(): boolean {
    return this.isDesktopView;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    // Triggers change detection so isDesktopView / gridDragScrollEnabled
    // re-evaluate and the template switches between the desktop grid and the
    // mobile card view at the 1024px breakpoint.
  }

  /**
   * Mobile card edit bridge for prototype task rows. Reuses the same store
   * update path as the grid's cell editing so the Save/Preview workflows see
   * identical state whether the user edited on desktop (grid) or mobile (card).
   */
  onPrototypeCardFieldChanged(task: IPrototypeGenerationRow): void {
    this.onPrototypeRowUpdated({ data: { ...task }, oldData: {} });
  }

  /**
   * Mobile card delete bridge for prototype task rows. Delegates to the exact
   * same handler the grid's Delete action uses, preserving the shared-taskId
   * guard for legacy rows without a rowId.
   */
  onPrototypeCardDelete(task: IPrototypeGenerationRow): void {
    this.deletePrototypeTask({ row: { data: task } });
  }

  get isReviewBatch(): boolean {
    return !!this.taskGenerationMetadata &&
      (this.taskGenerationMetadata.taskName ?? '').toUpperCase() === 'REVIEW';
  }

  files: File[] = [];
  requestTypes: any[];
  prototypeGenerationData$: Observable<any[]>;
  maturityDate$: Observable<Date | null>;
  referenceDate$: Observable<Date | null>;
  loanData$: Observable<any[]>;
  filteredLoanData$: Observable<any[]>;
  templateTypeData$: Observable<any>;
  templateData$: Observable<any>;
  frequencyOptions$: Observable<any>;
  frequencyOffsetOptions$: Observable<any>;
  formatOptions$: Observable<any>;
  defaultTasksForTemplate$: Observable<any>;
  selectedDefaultTasks$: Observable<any>;

  selectedPrototypeTasksSubscription: Subscription;
  generatedTasksSubscription: Subscription;
  loanNumberSubscription: Subscription;
  defaultTasksSubscription: Subscription;
  templateTypeSubscription: Subscription;
  templateDataSubscription: Subscription;
  defaultTasksForTemplateSubscription: Subscription;
  frequencyOptionsSubscription: Subscription;
  frequencyOffsetOptionsSubscription: Subscription;
  maturityDateSubscription: Subscription;
  formatOptionsSubscription: Subscription;
  reportEntityOptionsSubscription: Subscription;
  entityNameOptionsSubscription: Subscription;
  loanDataSubscription: Subscription;
  filteredLoanDataSubscription: Subscription;
  referenceDateSubscription: Subscription;
  loadingActionsSubscription: Subscription;
  lastPrototypeGenerateResultSubscription: Subscription;

  // Value sync subscriptions for form fields
  templateTypeValueSubscription: Subscription;
  categoryValueSubscription: Subscription;
  frequencyValueSubscription: Subscription;
  frequencyOffsetValueSubscription: Subscription;
  formatValueSubscription: Subscription;
  reportEntityValueSubscription: Subscription;
  entityNameValueSubscription: Subscription;
  reviewerListSubscription: Subscription;
  reviewerIdSubscription: Subscription;

  reviewerEditorOptions: any = {};

  selectedPrototypeTasks$: Observable<any>;
  selectedPrototypeTasks: IPrototypeGenerationRow[] = [];
  generatedTasks: IGeneratedTask[] = [];
  highlightedPrototypeRowIds: Set<string> = new Set<string>();
  lastPrototypeGenerateResult: any = { token: 0 };
  private highlightedRowsClearTimer: any;
  private latestGenerateTokenHandled = 0;
  private suppressAutoSelection = false;
  private lastFetchedTemplateType: string | null = null;
  private lastFetchedTemplateName: string | null = null;

  selectedTemplateType: string | null = null;
  selectedTemplateType$ = new BehaviorSubject<string | null>(null);
  selectedTemplate: string | null = null;
  selectedTemplate$ = new BehaviorSubject<string | null>(null);
  selectedDefaultTasks: string[] | null = null;

  reportEntityOptions$: Observable<any>;
  entityNameOptions$: Observable<any>;
  reviewerList$: Observable<{ id: string; name: string }[]>;
  selectedReviewerId$: Observable<string | null>;
  activeLoanCheckboxText: string = "Show Inactive";

  templateTypeEditorOptions: any = {
    items: [], // will be set from your observable
    valueExpr: 'templateType',
    displayExpr: 'templateType',
    required: true,
    searchMode: 'contains',
    searchEnabled: true,
    value: null,
    // Only fire for user-initiated changes; programmatic updates via templateTypeValueSubscription
    // would re-trigger fetchTemplates unnecessarily and expose frozen NgRx objects to DevExtreme.
    onValueChanged: (e: any) => { if (e.event) this.onTemplateTypeChanged(e); },
  };

  templateEditorOptions: any = {
    items: [], // will be set from observable
    valueExpr: undefined, // use the string value directly
    displayExpr: undefined, // use the string value directly
    required: true,
    searchMode: 'contains',
    searchEnabled: true,
    value: this.selectedTemplate,
    onValueChanged: (e: any) => this.onTemplateChanged(e),
  };

  defaultTasksEditorOptions: any = {
    items: [],
    valueExpr: 'taskId',
    displayExpr: 'taskName',
    required: true,
    searchMode: 'contains',
    searchEnabled: true,
    showSelectionControls: true,
    multiline: true,
    value: [],
    onValueChanged: (e: any) => this.onDefaultTasksChanged(e),
  };

  maturityDateOptions: any = {
    value: null, // This will be set from the observable in ngOnInit
    required: true,
    openOnFieldClick: true,
    displayFormat: 'MM/dd/yyyy',
    pickerType: 'calendar',
  };

  referenceDateOptions: any = {
    value: null, // This will be set from the observable in ngOnInit
    required: true,
    openOnFieldClick: true,
    displayFormat: 'MM/dd/yyyy',
    pickerType: 'calendar',
  };


  oneTimeDueDateOptions: any = {
    value: null,
    required: true,
    openOnFieldClick: true,
    displayFormat: 'MM/dd/yyyy',
    pickerType: 'calendar',
    onValueChanged: (e: any) => {
      // Dispatch setFrequencyOffsetValue action with the selected date
      this.store.dispatch(setFrequencyOffsetValue({ selectedFrequencyOffsetValue: e.value }));
    },
  };

  selectedFrequency: string | null = null;

  frequencyEditorOptions: any = {
    items: [], // will be set from observable
    valueExpr: undefined, // use the string value directly
    displayExpr: undefined, // use the string value directly
    required: true,
    searchMode: 'contains',
    searchEnabled: true,
    multiline: true,
    value: this.selectedFrequency,
    onValueChanged: (e: any) => {
      if (!e?.event) {
        return;
      }

      // Update local state
      this.selectedFrequency = e.value;

      // Dispatch action to update store
      this.store.dispatch(setFrequency({ selectedFrequency: e.value }));

      // Keep the editor option value aligned for user-initiated changes.
      this.frequencyEditorOptions = {
        ...this.frequencyEditorOptions,
        value: e.value
      };

      // Reset due only for user-initiated changes. Metadata/programmatic updates
      // should preserve the restored due selection.
      if (this.selectedFrequencyOffsetValue) {
        this.selectedFrequencyOffsetValue = null;
        this.store.dispatch(setFrequencyOffsetValue({ selectedFrequencyOffsetValue: null }));
      }
    },
  };

  selectedFrequencyOffsetValue: string | Date | null = null;

  frequencyOffsetEditorOptions: any = {
    items: [],
    displayExpr: 'label',
    valueExpr: 'value',
    required: true,
    searchMode: 'contains',
    searchEnabled: true,
    multiline: true,
    value: this.selectedFrequencyOffsetValue, // Initialize with the primitive value
    onValueChanged: (e: any) => {
      // Ignore programmatic/initialization changes that can clear metadata-restored values.
      if (!e?.event || e.value == null || e.value === '') {
        return;
      }
      this.store.dispatch(
        setFrequencyOffsetValue({ selectedFrequencyOffsetValue: e.value })
      );
    },
  };

  selectedFormatValue: string | null = null;

  formatEditorOptions: any = {
    items: [],
    displayExpr: undefined,
    valueExpr: undefined,
    required: true,
    searchMode: 'contains',
    searchEnabled: true,
    multiline: true,
    value: this.selectedFormatValue,
    onValueChanged: (e: any) => {
      this.store.dispatch(setFormatOption({ selectedFormatOption: e.value }));
    },
  };

  selectedReportEntityValue: string | null = null;

  reportEntityOptions: any = {
    items: [],
    displayExpr: 'label',
    valueExpr: 'value',
    required: true,
    searchMode: 'contains',
    searchEnabled: true,
    multiline: true,
    value: this.selectedReportEntityValue,
    onValueChanged: (e: any) => {
      if (!e?.event || e.value == null || e.value === '') {
        return;
      }
      this.selectedReportEntityValue = e.value;
      this.store.dispatch(setReportEntity({ selectedReportEntity: e.value }));
      this.syncEntityNameOptionsWithSelectedLoan(true);
    },
  };

  selectedEntityName: string | null = null;
  selectedLoanNumber$: Observable<any>; // Observable for the selected loan
  private availableLoans: any[] = [];
  private selectedLoanOption: any | null = null;
  private selectedLoanDetailSnapshot: any | null = null;
  showInactiveLoans: boolean = false; // Track whether to show inactive loans
  private showInactiveLoansSubject = new BehaviorSubject<boolean>(false);

  entityNameOptions: any = {
    items: [],
    displayExpr: 'label',
    valueExpr: 'label',
    required: true,
    searchMode: 'contains',
    acceptCustomValue: true,
    searchEnabled: true,
    multiline: true,
    value: this.selectedEntityName,
  };

  // Method to handle custom text values in the entityName SelectBox
  onEntityNameCustomItemCreating = (e) => {
    const newItem = {
      label: e.text,
      value: e.text
    };

    // Add the custom item to the existing items
    this.customEntityNameOptions.items = [...this.customEntityNameOptions.items, newItem];

    // Set the new item
    e.customItem = newItem;

    // Dispatch action to update the selected entity name in the store
    this.store.dispatch(setEntityName({ selectedEntityName: e.text }));

    return newItem;
  };

  // Custom entity name options with additional properties for custom value input
  customEntityNameOptions: any = {
    items: [],
    displayExpr: 'label',
    valueExpr: 'label',
    required: true,
    searchMode: 'contains',
    acceptCustomValue: true,
    searchEnabled: true,
    multiline: true,
    value: this.selectedEntityName,
    onValueChanged: (e: any) => {
      this.store.dispatch(setEntityName({ selectedEntityName: e.value }));
    },
    onCustomItemCreating: this.onEntityNameCustomItemCreating.bind(this)
  };

  readonlyLoanNumberEditorOptions: any = {
    value: 'N/A',
    disabled: true,
  };

  constructor(
    private readonly taskDataService: TaskDataService,
    private readonly loggingService: LoggingService,
    private readonly messageService: MessageService,
    private frequencyOptionsService: FrequencyOptionsService,
    private actions$: Actions,
    private store: Store<IFandCGeneratorState>
  ) {

    // Initialize observables
    this.templateTypeData$ = this.store.select(selectTemplateTypes);
    this.templateData$ = this.store.select(selectTemplates);
    this.frequencyOptions$ = this.store.select(selectFrequencyOptions);
    this.formatOptions$ = this.store.select(selectFormatOptions);
    this.frequencyOffsetOptions$ = this.store.select(selectFrequencyOffsetOptions);
    this.defaultTasksForTemplate$ = this.store.select(selectDefaultTasksForTemplate);
    this.selectedDefaultTasks$ = this.store.select(selectSelectedDefaultTasks);
    this.maturityDate$ = this.store.select(selectMaturityDate);
    this.referenceDate$ = this.store.select(selectReferenceDate);
    this.loanData$ = this.store.select(selectLoans);
    this.selectedLoanNumber$ = this.store.select(selectSelectedLoanDetail); // Initialize the selected loan observable with full loan object
    this.reportEntityOptions$ = this.store.select(selectReportEntityOptions);
    this.entityNameOptions$ = this.store.select(selectEntityNameOptions);
    this.reviewerList$ = this.store.select(selectReviewerList);
    this.selectedReviewerId$ = this.store.select(selectSelectedReviewerId);

    this.fandCRequestData = (this.fandCRequestData ?? {}) as any;

    // Create filtered loans observable that responds to checkbox state
    this.filteredLoanData$ = combineLatest([
      this.loanData$,
      this.showInactiveLoansSubject.asObservable()
    ]).pipe(
      map(([loans, showInactive]) => {
        if (!loans) return [];
        return showInactive ? loans : loans.filter(loan => loan.status !== 'Inactive');
      })
    );

    // Set up subscriptions that need immediate activation
    this.selectedPrototypeTasksSubscription = this.store
      .select(selectSelectedPrototypeTasks)
      .subscribe((result) => {
        // console.log('Selected Prototype Tasks:', result);
        const selectedPrototypeTasks = result
          ? result.map((x) => ({ ...x }))
          : [];

        this.selectedPrototypeTasks = selectedPrototypeTasks;
      });

    this.lastPrototypeGenerateResultSubscription = this.store
      .select(selectLastPrototypeGenerateResult)
      .subscribe((result) => {
        if (!result || result.token <= this.latestGenerateTokenHandled) {
          return;
        }

        this.lastPrototypeGenerateResult = result;
        this.latestGenerateTokenHandled = result.token;

        if (result.addedCount === 0 && result.duplicateRowIds.length > 0) {
          notify('Nothing to generate; no data has changed.', 'info', getNotificationTimeout());
          this.highlightRows(result.duplicateRowIds);
          return;
        }

        this.clearHighlightedRows();
      });

    this.generatedTasksSubscription = this.store
      .select(selectGeneratedTasks)
      .subscribe((result) => {
        // console.log('Generated Tasks:', result);
        this.generatedTasks = result ? result.map((x) => ({ ...x })) : [];
      });

    // Subscribe to selected loan to track if it's populated
    this.loanNumberSubscription = this.selectedLoanNumber$.subscribe(loanDetail => {
      this.selectedLoanDetailSnapshot = loanDetail;
      this.isLoanNumberPopulated = !!loanDetail;
      this.loanDisplayText =
        loanDetail?.facilityDetail?.name ??
        loanDetail?.facilityDetail?.Name ??
        loanDetail?.name ??
        loanDetail?.Name ??
        '';
      this.readonlyLoanNumberEditorOptions = {
        ...this.readonlyLoanNumberEditorOptions,
        value: this.loanDisplayText || 'N/A',
        disabled: true,
      };
      // Turn off metadata loading when loan data arrives
      if (loanDetail && this.isLoadingMetadata) {
        this.isLoadingMetadata = false;
      }

      this.syncEntityNameOptionsWithSelectedLoan(false);
    });

    // Subscribe to selectedDefaultTasks to track if any are selected
    this.defaultTasksSubscription = this.selectedDefaultTasks$.subscribe(tasks => {
      const selectedTaskIds = Array.isArray(tasks) ? [...tasks] : [];
      const currentSelectedTaskIds = Array.isArray(this.selectedDefaultTasks)
        ? this.selectedDefaultTasks
        : [];
      const hasSelectionChanged = !this.arePrimitiveArraysEqual(selectedTaskIds, currentSelectedTaskIds);

      this.isTaskTypesSelected = selectedTaskIds.length > 0;

      if (!hasSelectionChanged) {
        return;
      }

      this.syncDefaultTasksSelection(selectedTaskIds);
    });
  }

  onTemplateTypeChanged(e: any) {
    const nextTemplateType = (e?.value ?? null) as string | null;
    if (!nextTemplateType) {
      return;
    }

    if (this.lastFetchedTemplateType === nextTemplateType) {
      return;
    }

    this.selectedTemplateType = nextTemplateType;
    this.lastFetchedTemplateType = nextTemplateType;
    this.lastFetchedTemplateName = null;

    this.store.dispatch(
      fetchTemplates({
        criteria: {
          referenceTypeName: 'FandC',
          templateName: nextTemplateType,
        },
      })
    );
    //this.selectedTemplateType$.next(e.value);
  }

  onTemplateChanged(e: any) {
    const nextTemplate = (e?.value ?? null) as string | null;
    if (!nextTemplate) {
      return;
    }

    if (this.lastFetchedTemplateName === nextTemplate) {
      return;
    }

    this.selectedTemplate = nextTemplate;
    this.lastFetchedTemplateName = nextTemplate;
    this.store.dispatch(setCategory({ category: nextTemplate }));
    this.fandCRequestData = {
      ...(this.fandCRequestData || {}),
      category: nextTemplate,
    } as any;

    // Extract the first word of the selected template
    const firstWord = nextTemplate.split(' ')[0];

    // console.log(firstWord);
    // Check if this word matches any frequency option
    if (this.frequencyEditorOptions && this.frequencyEditorOptions.items) {
      const matchingFrequency = this.frequencyEditorOptions.items.find(
      frequency => frequency.startsWith(firstWord)
      );

      // console.log('Matching Frequency:', matchingFrequency);

      // If there's a match, update the frequency selection
      if (matchingFrequency) {
        this.selectedFrequency = matchingFrequency;
        this.store.dispatch(setFrequency({ selectedFrequency: matchingFrequency }));

        // Update the frequency editor options to reflect the new value on the UI
        this.frequencyEditorOptions = {
          ...this.frequencyEditorOptions,
          value: this.selectedFrequency
        };
      }
    }

    // Dispatch setCategory with the template name to ensure the category is set in the store
    this.store.dispatch(
      fetchDefaultTasksForTemplate({
        criteria:
          {
            templateName: nextTemplate,
          },
      })
    );
  }

  onDefaultTasksChanged(e: any) {
    // Ignore dxForm reconciliation events; only user TagBox changes update task state.
    if (!e?.event) {
      return;
    }

    // Only the task TagBox emits an array value. Other form editors use scalar values.
    if (e?.value != null && !Array.isArray(e.value)) {
      return;
    }

    const selectedItems = Array.isArray(e?.value) ? e.value : [];
    const selectedTaskIds = selectedItems
      .map((item: any) => item?.taskId ?? item)
      .filter((taskId: any) => taskId != null && taskId !== '');

    const currentSelectedTaskIds = Array.isArray(this.selectedDefaultTasks)
      ? this.selectedDefaultTasks
      : [];

    const hasChanged = !this.arePrimitiveArraysEqual(
      selectedTaskIds,
      currentSelectedTaskIds
    );

    if (!hasChanged) {
      if (this.selectedDefaultTasks == null) {
        this.selectedDefaultTasks = [];
        this.isTaskTypesSelected = false;
      }
      return;
    }

    this.selectedDefaultTasks = selectedTaskIds;
    this.isTaskTypesSelected = selectedTaskIds.length > 0;

    // Keep the editor and form model in sync immediately. The dxForm can emit
    // its field event before the NgRx selector subscription observes the
    // update, which otherwise leaves the TagBox visually unselected and keeps
    // the request payload stale.
    this.syncDefaultTasksSelection(selectedTaskIds);

    this.store.dispatch(
      updateSelectedDefaultTasks({ data: this.selectedDefaultTasks })
    );
  }

  onPrototypeRowUpdated(e: any) {
    const mergedData = { ...(e.oldData || {}), ...(e.data || {}) };
    this.store.dispatch(updatePrototypeTasks({ changeData: mergedData }));
  }

  onGeneratedTaskRowUpdated(e: any) {
    this.store.dispatch(updateGeneratedTasks({ changeData: { ...e.data } }));
  }

  onFileUpload(file: File) {
    this.files.push(file);
  }

  removePrototypeTask(rowId: string) {
    this.store.dispatch(removePrototypeTask({ rowId }));
  }

  deletePrototypeTask = (e: any) => {
    const rowId = e.row.data.rowId;
    if (rowId) {
      this.store.dispatch(removePrototypeTask({ rowId }));
      return;
    }
    const sharedCount = (this.selectedPrototypeTasks || []).filter(
      (t: any) => t.taskId === e.row.data.taskId
    ).length;
    if (sharedCount > 1) {
      return;
    }
    this.store.dispatch(removePrototypeTask({ rowId: e.row.data.taskId }));
  }

  async onSubmit(e: any) {
    e.preventDefault();

    // let validationResult = validationEngine.validateGroup('formGroup');

    // if (validationResult.isValid) {
    //   try {
    //     let request: ITaskRequest = new TaskRequest();
    //     let requestType = this.requestTypes.find(
    //       (t) => t.name == this.fandCRequestData.requestType
    //     );
    //     request.templateId = requestType.id;

    //     request.name = requestType.name;
    //     request.referenceName = this.fandCRequestData.loanNumber;
    //     request.referenceType = requestType.referenceType;

    //     let formData: FormData = new FormData();
    //     formData.append('templateId', request.templateId);
    //     formData.append('locations', request.locations);
    //     formData.append('name', request.name);
    //     formData.append('referenceName', request.referenceName);
    //     formData.append(
    //       'requestJsonData',
    //       JSON.stringify(this.fandCRequestData)
    //     );
    //     formData.append('referenceType', request.referenceType);

    //     this.files.forEach((f) => {
    //       formData.append('files', f, f.name);
    //     });

    //     this.isLoading = true;

    //     const response = await lastValueFrom(
    //       this.taskDataService.insertTaskAsFormData(formData)
    //     );
    //     sendNotificationMessage(
    //       `${response[1].data[0].task.taskId}} task created`,
    //       this.messageService
    //     );

    //     this.isLoading = false;

    //     this.formElement.nativeElement.reset();
    //     this.formElement.nativeElement.submit();
    //   } catch (err: any) {
    //     this.isLoading = false;
    //     this.loggingService.logError(err, SeverityLevel.Error);
    //     notify(err.message, 'error', getNotificationTimeout());
    //   }
    // }
  }

  /**
   * Validates the shared DevExtreme validation group ("formGroup") used by the
   * F&C form. The Generate, Preview tasks, and Save buttons use
   * `useSubmitBehavior: false`, so DevExtreme does not validate the group on
   * click. Each action handler must call this guard so required fields (such as
   * the "Due" field) block the action and surface the standard validation
   * feedback before any HTTP request is dispatched.
   */
  private isFormGroupValid(): boolean {
    const validationResult = validationEngine.validateGroup('formGroup');
    return !!validationResult?.isValid;
  }

  addPrototypeTasks() {
    if (!this.isFormGroupValid()) {
      return;
    }

    const currentReferenceDate = (this.fandCRequestData as any)?.referenceDate;
    if (currentReferenceDate) {
      this.store.dispatch(
        setReferenceDate({ selectedReferenceDate: currentReferenceDate })
      );
    }

    const currentMaturityDate = (this.fandCRequestData as any)?.maturityDate;
    if (currentMaturityDate) {
      this.store.dispatch(
        setMaturityDate({ selectedMaturityDate: currentMaturityDate })
      );
    }

    const currentLoanNumber = (this.fandCRequestData as any)?.loanNumber;
    if (currentLoanNumber) {
      this.store.dispatch(
        setLoanNumber({ selectedLoan: currentLoanNumber as any })
      );
    }

    this.store.dispatch(addToSelectedPrototypeTasks());
  }

  saveGenerationTask() {
    if (!this.isFormGroupValid()) {
      return;
    }

    this.isSavingGenerationTask = true;
    this.store.select(selectExistingTaskIds).pipe(take(1)).subscribe(existingTaskIds => {
      if (Array.isArray(existingTaskIds) && existingTaskIds.length > 0) {
        this.store.dispatch(updateBatch());
      } else {
        this.store.dispatch(saveGenerationMetadata());
      }
    });
  }

  generateTasks() {
    if (!this.isFormGroupValid()) {
      return;
    }

    this.isGeneratingTasks = true;
    this.store.dispatch(generateTasks());
  }

  saveTasks() {
    this.isSavingTasks = true;
    this.store.dispatch(saveGeneratedTasks());
  }

  async  ngOnInit() {
    this.suppressAutoSelection = !!this.taskGenerationMetadata;
    this.store.dispatch(resetFandCGenerator());

    this.store.dispatch(
      fetchRootTemplate({ templateName: this.taskTemplateName })
    );

    this.loadingActionsSubscription = this.actions$
      .pipe(
        ofType(
          fetchLoansSuccess,
          fetchGeneratedTasksSuccess,
          saveGeneratedTasksSuccess,
          fetchGenerationMetadataSuccess,
          saveGenerationTaskSuccess,
          updateBatchSuccess
        )
      )
      .subscribe((action) => {
        if (action.type === fetchLoansSuccess.type) {
          this.isLoadingLoanData = false;
        }
        if (action.type === fetchGeneratedTasksSuccess.type) {
          this.isGeneratingTasks = false;
          this.scrollToEnd();
        }
        if (action.type === saveGeneratedTasksSuccess.type) {
          this.isSavingTasks = false;
        }
        if (action.type === updateBatchSuccess.type) {
          this.isSavingTasks = false;
          this.isSavingGenerationTask = false;
        }
        if (action.type === fetchGenerationMetadataSuccess.type) {
          // Only clear the prototype-task input fields when opened from a Review task (add-more flow).
          if (this.isReviewBatch) {
            this.store.dispatch(clearEditModeFormSelections());
            this.clearEditModeInputs();
          }
        }
        if (action.type === saveGenerationTaskSuccess.type) {
          // Clear saving indicator when save operation completes
          this.isSavingGenerationTask = false;
          // console.log('Generation task saved successfully');

          // Show success notification to the user
          notify('Generation task saved successfully', 'success', getNotificationTimeout());
        }
      });

    // Only fetch loans if we don't have metadata
    if (!this.taskGenerationMetadata) {
      this.isLoadingLoanData = true;
      this.store.dispatch(fetchLoans());
    }

    this.store.dispatch(
      fetchTemplateTypes({ criteria: { referenceTypeName: 'FandC' } })
    );

    this.store.dispatch(fetchFrequencyOptions());
    this.store.dispatch(fetchFormatOptions());
    this.store.dispatch(fetchReviewers());
    this.store.dispatch(fetchReportEntityOptions());

    // Initialize form data once before subscriptions begin pushing store values.
    // This prevents later default initialization from overwriting restored metadata values.
    this.initializeFandCRequestData();

    try {
      // Set up subscriptions for updating editor options and other component properties
      this.templateTypeSubscription = this.templateTypeData$.subscribe((items) => {
        const mutableItems = Array.isArray(items) ? items.map((x: any) => ({ ...x })) : [];
        const currentValue = this.templateTypeEditorOptions?.value;
        const nextValue = this.getAutoSelectedValue(mutableItems, currentValue, 'templateType');

        this.templateTypeEditorOptions = {
          ...this.templateTypeEditorOptions,
          items: mutableItems,
          value: nextValue,
        };

        if (nextValue && this.lastFetchedTemplateType !== nextValue && !this.suppressAutoSelection) {
          this.onTemplateTypeChanged({ value: nextValue });
        }
      });

      this.templateDataSubscription = this.templateData$.subscribe((items) => {
        const mutableItems = Array.isArray(items) ? items.map(item => item.name) : [];
        const currentValue = this.templateEditorOptions?.value;
        const selectedValue = this.getAutoSelectedValue(mutableItems, currentValue);

        this.templateEditorOptions = {
          ...this.templateEditorOptions,
          items: mutableItems,
          value: selectedValue,
        };

        if (selectedValue && this.lastFetchedTemplateName !== selectedValue && !this.suppressAutoSelection) {
          this.onTemplateChanged({ value: selectedValue });
        }
      });

      this.defaultTasksForTemplateSubscription = this.defaultTasksForTemplate$.subscribe((result) => {
        // console.log(result);
        const sortedItems = (result || []).map((x: any) => ({ ...x })).sort((a: any, b: any) => {
          if (a.taskName < b.taskName) return -1;
          if (a.taskName > b.taskName) return 1;
          return 0;
        });

        const currentValue = Array.isArray(this.selectedDefaultTasks)
          ? this.selectedDefaultTasks
          : [];

        // Pre-select items matching existing prototype tasks when restoring from metadata.
        let nextValue: any[];
        if (this.taskGenerationMetadata && currentValue.length === 0 && this.selectedPrototypeTasks?.length > 0) {
          const protoNames = new Set(this.selectedPrototypeTasks.map((t: any) => t.taskName));
          const preSelected = sortedItems.filter((item: any) => protoNames.has(item.taskName)).map((item: any) => item.taskId);
          nextValue = preSelected.length > 0 ? preSelected : this.getAutoSelectedTagBoxValues(sortedItems, currentValue, 'taskId');
        } else {
          nextValue = this.getAutoSelectedTagBoxValues(sortedItems, currentValue, 'taskId');
        }

        this.patchDefaultTasksEditorOptions(sortedItems);

        const currentEditorValue = Array.isArray(this.defaultTasksEditorOptions.value)
          ? this.defaultTasksEditorOptions.value
          : [];
        if (!this.arePrimitiveArraysEqual(nextValue, currentEditorValue)) {
          this.defaultTasksEditorOptions = {
            ...this.defaultTasksEditorOptions,
            value: [...nextValue],
          };
        }

        this.isTaskTypesSelected = nextValue.length > 0;

        if (!this.arePrimitiveArraysEqual(nextValue, currentValue)) {
          this.selectedDefaultTasks = nextValue;
          this.store.dispatch(updateSelectedDefaultTasks({ data: nextValue }));
          this.fandCRequestData = {
            ...(this.fandCRequestData || {}),
            taskTypes: nextValue,
          } as any;
        }
      });

      this.frequencyOptionsSubscription = this.frequencyOptions$.subscribe((items) => {
        // items is an array of strings, so just clone the array (no need to map/spread objects)
        const mutableItems = Array.isArray(items) ? [...items] : [];
        const currentValue = this.frequencyEditorOptions?.value;
        const nextValue = this.getAutoSelectedValue(mutableItems, currentValue);
        // console.log('Frequency Options:', mutableItems);
        this.frequencyEditorOptions = {
          ...this.frequencyEditorOptions,
          items: mutableItems,
          value: nextValue,
        };

        if (nextValue !== currentValue) {
          this.selectedFrequency = nextValue;
          this.store.dispatch(setFrequency({ selectedFrequency: nextValue }));
        }
      });

      this.frequencyOffsetOptionsSubscription = this.frequencyOffsetOptions$.subscribe((items) => {
        const mutableItems = (items || []).map((x) => ({ ...x }));
        const currentValue = this.selectedFrequencyOffsetValue;
        const selectedValue = this.getAutoSelectedValue(mutableItems, currentValue, 'value');

        this.frequencyOffsetEditorOptions = {
          ...this.frequencyOffsetEditorOptions,
          items: mutableItems,
          value: selectedValue,
        };

        if (selectedValue !== currentValue) {
          this.selectedFrequencyOffsetValue = selectedValue;
          this.store.dispatch(
            setFrequencyOffsetValue({ selectedFrequencyOffsetValue: selectedValue })
          );
        }
      });

      this.maturityDateSubscription = this.maturityDate$.subscribe((date) => {
        const currentMaturityDate = (this.fandCRequestData as any)?.maturityDate ?? null;
        const nextMaturityDate = date ?? currentMaturityDate;

        this.maturityDateOptions = {
          ...this.maturityDateOptions,
          value: nextMaturityDate,
        };

        this.fandCRequestData = {
          ...(this.fandCRequestData || {}),
          maturityDate: nextMaturityDate,
        } as any;
      });

      this.formatOptionsSubscription = this.formatOptions$.subscribe((items) => {
        const mutableItems = Array.isArray(items) ? [...items] : [];
        const currentValue = this.formatEditorOptions?.value;
        const nextValue = this.getAutoSelectedValue(mutableItems, currentValue);

        this.formatEditorOptions = {
          ...this.formatEditorOptions,
          items: mutableItems,
          value: nextValue,
        };

        if (nextValue !== currentValue) {
          this.store.dispatch(setFormatOption({ selectedFormatOption: nextValue }));
        }
      });

      this.reportEntityOptionsSubscription = this.reportEntityOptions$.subscribe((items) => {
        const currentValue = (this.selectedReportEntityValue ?? this.reportEntityOptions?.value ?? '')
          .toString()
          .trim();
        const fallbackLabelMap: Record<string, string> = {
          borrowers: 'Borrower',
          guarantors: 'Guarantor',
          jointVentures: 'Joint Venture',
          lenders: 'Lender',
          loanParties: 'Loan Parties',
          masterTenants: 'Master Tenant',
          operatingLessees: 'Operating Lessee',
          properties: 'Property',
        };

        const incomingItems = Array.isArray(items) ? [...items] : [];
        const hasCurrentValue = currentValue
          ? incomingItems.some((item: any) => item?.value === currentValue)
          : true;

        const mergedItems = !hasCurrentValue && currentValue
          ? [
              ...incomingItems,
              {
                label: fallbackLabelMap[currentValue] ?? currentValue,
                value: currentValue,
              },
            ]
          : incomingItems;

        const nextValue = this.getAutoSelectedValue(mergedItems, currentValue, 'value');

        this.reportEntityOptions = {
          ...this.reportEntityOptions,
          items: mergedItems,
          value: nextValue,
        };

        if (nextValue !== currentValue) {
          this.selectedReportEntityValue = nextValue;
          this.store.dispatch(setReportEntity({ selectedReportEntity: nextValue }));
        }
      });

      this.entityNameOptionsSubscription = this.entityNameOptions$.subscribe((items) => {
        const mutableItems = Array.isArray(items) ? [...items] : [];
        const currentValue = this.customEntityNameOptions?.value;
        const selectedReportEntity =
          this.selectedReportEntityValue ??
          (this.fandCRequestData as any)?.reportEntity ??
          this.reportEntityOptions?.value ??
          null;
        const derivedItems = this.getDerivedEntityNameOptions(selectedReportEntity);
        const mergedItems = this.mergeEntityNameOptions(mutableItems, derivedItems, currentValue);
        const nextValue = derivedItems.length > 0 && !this.isValueInItems(derivedItems, currentValue, 'label')
          ? derivedItems[0].label
          : this.getAutoSelectedValue(mergedItems, currentValue, 'label');

        this.entityNameOptions = {
          ...this.entityNameOptions,
          items: mergedItems,
          value: nextValue,
        };

        // Update customEntityNameOptions with the same items
        this.customEntityNameOptions = {
          ...this.customEntityNameOptions,
          items: mergedItems,
          value: nextValue,
        };

        this.fandCRequestData = {
          ...(this.fandCRequestData || {}),
          entityName: nextValue,
        } as any;

        if (nextValue !== currentValue) {
          this.store.dispatch(setEntityName({ selectedEntityName: nextValue }));
        }
      });

      this.loanDataSubscription = this.loanData$.subscribe((items) => {
        this.availableLoans = Array.isArray(items) ? items : [];
        // console.log('Loan Data:', items);
      });

      this.filteredLoanDataSubscription = this.filteredLoanData$.subscribe((items) => {
        const loans = Array.isArray(items) ? items : [];
        const currentLoanValue = (this.fandCRequestData as any)?.loanNumber ?? null;
        const nextLoanValue = this.getAutoSelectedValue(loans, currentLoanValue, 'name');

        if (nextLoanValue !== currentLoanValue) {
          const selectedLoan = loans.find((loan: any) => loan?.name === nextLoanValue) ?? nextLoanValue;
          this.selectedLoanOption = selectedLoan;
          const selectedLoanId =
            selectedLoan?.id ??
            selectedLoan?.Id ??
            selectedLoan?.facilityId ??
            selectedLoan?.FacilityId ??
            null;

          this.fandCRequestData = {
            ...(this.fandCRequestData || {}),
            loanNumber: nextLoanValue,
          } as any;

          this.store.dispatch(setLoanNumber({ selectedLoan }));
          this.syncEntityNameOptionsWithSelectedLoan(true);
          if (selectedLoanId) {
            this.store.dispatch(fetchLoanDetail({ facilityId: selectedLoanId }));
          }
        }
      });

      this.referenceDateSubscription = this.referenceDate$.subscribe((date) => {
        const currentReferenceDate = (this.fandCRequestData as any)?.referenceDate ?? null;
        const nextReferenceDate = date ?? currentReferenceDate;

        this.referenceDateOptions = {
          ...this.referenceDateOptions,
          value: nextReferenceDate,
        };

        this.fandCRequestData = {
          ...(this.fandCRequestData || {}),
          referenceDate: nextReferenceDate,
        } as any;
      });

      // Add subscriptions to sync actual values from store to editor options
      this.templateTypeValueSubscription = this.store.select(selectSelectedTemplateType).subscribe((templateType) => {
        this.templateTypeEditorOptions = {
          ...this.templateTypeEditorOptions,
          value: templateType
        };

        this.fandCRequestData = {
          ...(this.fandCRequestData || {}),
          selectedTemplateType: templateType,
        } as any;
      });

      this.categoryValueSubscription = this.store.select(selectCategory).subscribe((category) => {
        this.templateEditorOptions = {
          ...this.templateEditorOptions,
          value: category
        };

        this.fandCRequestData = {
          ...(this.fandCRequestData || {}),
          category,
        } as any;
      });

      this.frequencyValueSubscription = this.store.select(selectedFrequency).subscribe((frequency) => {
        this.selectedFrequency = frequency;
        this.frequencyEditorOptions = {
          ...this.frequencyEditorOptions,
          value: frequency
        };

        if ((this.fandCRequestData as any)?.frequency !== frequency) {
          (this.fandCRequestData as any).frequency = frequency;
        }
      });

      this.frequencyOffsetValueSubscription = this.store.select(selectFrequencyOffsetValue).subscribe((offsetValue) => {
        this.selectedFrequencyOffsetValue = offsetValue;
        this.frequencyOffsetEditorOptions = {
          ...this.frequencyOffsetEditorOptions,
          value: offsetValue
        };
        this.oneTimeDueDateOptions = {
          ...this.oneTimeDueDateOptions,
          value: (offsetValue && typeof offsetValue === 'object' && 'getTime' in (offsetValue as any)) ? offsetValue as Date : null
        };

        this.fandCRequestData = {
          ...(this.fandCRequestData || {}),
          frequencyOffsetOption:
            offsetValue && !(typeof offsetValue === 'object' && 'getTime' in (offsetValue as any))
              ? offsetValue
              : null,
          oneTimeDueDate:
            offsetValue && typeof offsetValue === 'object' && 'getTime' in (offsetValue as any)
              ? (offsetValue as Date)
              : null,
        } as any;
      });

      this.formatValueSubscription = this.store.select(selectSelectedFormatOption).subscribe((format) => {
        this.formatEditorOptions = {
          ...this.formatEditorOptions,
          value: format
        };

        this.fandCRequestData = {
          ...(this.fandCRequestData || {}),
          format,
        } as any;
      });

      this.reportEntityValueSubscription = this.store.select(selectSelectedReportEntity).subscribe((reportEntity) => {
        const existingValue =
          (this.selectedReportEntityValue ?? (this.fandCRequestData as any)?.reportEntity ?? null) as string | null;
        const safeReportEntity =
          reportEntity == null || reportEntity === '' ? existingValue : reportEntity;

        this.selectedReportEntityValue = safeReportEntity;
        const normalizedValue = (safeReportEntity ?? '').toString().trim();
        const fallbackLabelMap: Record<string, string> = {
          borrowers: 'Borrower',
          guarantors: 'Guarantor',
          jointVentures: 'Joint Venture',
          lenders: 'Lender',
          loanParties: 'Loan Parties',
          masterTenants: 'Master Tenant',
          operatingLessees: 'Operating Lessee',
          properties: 'Property',
        };

        const existingItems = Array.isArray(this.reportEntityOptions?.items)
          ? this.reportEntityOptions.items
          : [];

        const hasValueInItems = normalizedValue
          ? existingItems.some((item: any) => item?.value === normalizedValue)
          : true;

        const mergedItems = !hasValueInItems && normalizedValue
          ? [
              ...existingItems,
              {
                label: fallbackLabelMap[normalizedValue] ?? normalizedValue,
                value: normalizedValue,
              },
            ]
          : existingItems;

        this.reportEntityOptions = {
          ...this.reportEntityOptions,
          items: mergedItems,
          value: safeReportEntity
        };

        this.fandCRequestData = {
          ...(this.fandCRequestData || {}),
          reportEntity: safeReportEntity,
        } as any;

        this.syncEntityNameOptionsWithSelectedLoan(safeReportEntity !== existingValue);
      });

      this.entityNameValueSubscription = this.store.select(selectSelectedEntityName).subscribe((entityName) => {
        this.customEntityNameOptions = {
          ...this.customEntityNameOptions,
          value: entityName
        };

        this.fandCRequestData = {
          ...(this.fandCRequestData || {}),
          entityName,
        } as any;
      });

      this.reviewerEditorOptions = {
        displayExpr: 'name',
        valueExpr: 'id',
        searchEnabled: true,
        searchMode: 'contains',
        placeholder: 'Select reviewer',
        showClearButton: true,
        items: [],
        onValueChanged: this.onReviewerChanged.bind(this),
      };

      this.reviewerListSubscription = this.reviewerList$.subscribe(users => {
        this.reviewerEditorOptions = { ...this.reviewerEditorOptions, items: users };
      });

      this.reviewerIdSubscription = this.store.select(selectSelectedReviewerId).subscribe(id => {
        this.reviewerEditorOptions.value = id;
      });

      // set values from metadata if available
      this.setStateFromMetadata();
    } catch (err: any) {
      this.loggingService.logError(err, SeverityLevel.Error);
      notify(err.message, 'error', getNotificationTimeout());
    }
  }

  private initializeFandCRequestData(): void {
    this.fandCRequestData = new TreasuryRequestData();
    // Add additional properties that are used in the form but not in the base interface.
    (this.fandCRequestData as any).oneTimeDueDate = null;
    (this.fandCRequestData as any).selectedTemplateType = null;
    (this.fandCRequestData as any).category = null;
    (this.fandCRequestData as any).frequency = null;
    (this.fandCRequestData as any).frequencyOffsetOption = null;
    (this.fandCRequestData as any).format = null;
    (this.fandCRequestData as any).reportEntity = null;
    (this.fandCRequestData as any).entityName = null;
    (this.fandCRequestData as any).referenceDate = null;
    (this.fandCRequestData as any).maturityDate = null;
    (this.fandCRequestData as any).taskTypes = null;
  }

  setStateFromMetadata() {
    // Set state from metadata if present
    if (this.taskGenerationMetadata) {
      // Set loading flag while fetching metadata
      this.isLoadingMetadata = true;

      this.store.dispatch(setTaskId({ taskId: this.taskGenerationMetadata.taskId }));
      this.store.dispatch(fetchGenerationMetadata({ metadataId: this.taskGenerationMetadata.taskGenerationMetadataId }));

      // If we have metadata, the loan number should be populated
      this.isLoanNumberPopulated = true;

      // The selected task types will be populated via the store subscription
      // when the metadata is loaded

      // if (meta.selectedLoanNumber) {
      //   this.store.dispatch(
      //     setLoanNumber({ selectedLoan: meta.selectedLoanNumber })
      //   );
      // }

      // if (meta.selectedPrototypeTasks) {
      //   this.store.dispatch(
      //     setSelectedPrototypeTasks({ selectedPrototypeTasks: meta.selectedPrototypeTasks })
      //   );
      // }
    }
  }

  scrollToEnd() {
    if (this.scrollView && this.scrollView.instance) {
      this.scrollView.instance.scrollTo({ top: Number.MAX_SAFE_INTEGER });
    }
  }

  onReviewerChanged(e: any): void {
    const id = e.value ?? null;
    if (id == null) {
      this.store.dispatch(setReviewer({ reviewerId: null, reviewerName: null }));
      return;
    }
    const selectedItem = (this.reviewerEditorOptions.items as any[])?.find((u: any) => u.id === id);
    this.store.dispatch(setReviewer({
      reviewerId: id,
      reviewerName: selectedItem?.name ?? null,
    }));
  }

  ngOnDestroy() {
    // Clean up all subscriptions to prevent memory leaks
    this.unsubscribeIfExists(this.loadingActionsSubscription);
    this.unsubscribeIfExists(this.selectedPrototypeTasksSubscription);
    this.unsubscribeIfExists(this.generatedTasksSubscription);
    this.unsubscribeIfExists(this.loanNumberSubscription);
    this.unsubscribeIfExists(this.defaultTasksSubscription);
    this.unsubscribeIfExists(this.templateTypeSubscription);
    this.unsubscribeIfExists(this.templateDataSubscription);
    this.unsubscribeIfExists(this.defaultTasksForTemplateSubscription);
    this.unsubscribeIfExists(this.frequencyOptionsSubscription);
    this.unsubscribeIfExists(this.frequencyOffsetOptionsSubscription);
    this.unsubscribeIfExists(this.maturityDateSubscription);
    this.unsubscribeIfExists(this.formatOptionsSubscription);
    this.unsubscribeIfExists(this.reportEntityOptionsSubscription);
    this.unsubscribeIfExists(this.entityNameOptionsSubscription);
    this.unsubscribeIfExists(this.loanDataSubscription);
    this.unsubscribeIfExists(this.filteredLoanDataSubscription);
    this.unsubscribeIfExists(this.referenceDateSubscription);
    this.unsubscribeIfExists(this.lastPrototypeGenerateResultSubscription);

    // Clean up value sync subscriptions
    this.unsubscribeIfExists(this.templateTypeValueSubscription);
    this.unsubscribeIfExists(this.categoryValueSubscription);
    this.unsubscribeIfExists(this.frequencyValueSubscription);
    this.unsubscribeIfExists(this.frequencyOffsetValueSubscription);
    this.unsubscribeIfExists(this.formatValueSubscription);
    this.unsubscribeIfExists(this.reportEntityValueSubscription);
    this.unsubscribeIfExists(this.entityNameValueSubscription);
    this.unsubscribeIfExists(this.reviewerListSubscription);
    this.unsubscribeIfExists(this.reviewerIdSubscription);

    if (this.highlightedRowsClearTimer) {
      clearTimeout(this.highlightedRowsClearTimer);
      this.highlightedRowsClearTimer = null;
    }
  }

  // Helper method to safely unsubscribe
  private unsubscribeIfExists(subscription: Subscription): void {
    if (subscription) {
      subscription.unsubscribe();
    }
  }

  private getAutoSelectedValue(items: any[], currentValue: any, valueExpr?: string): any {
    if (this.suppressAutoSelection) {
      return currentValue;
    }

    if (!Array.isArray(items) || items.length === 0) {
      return currentValue;
    }

    if (items.length !== 1) {
      return this.isValueInItems(items, currentValue, valueExpr) ? currentValue : currentValue;
    }

    const singleValue = this.extractItemValue(items[0], valueExpr);
    if (singleValue == null) {
      return currentValue;
    }

    if (currentValue == null || currentValue === '' || !this.isValueInItems(items, currentValue, valueExpr)) {
      return singleValue;
    }

    return currentValue;
  }

  private getAutoSelectedTagBoxValues(items: any[], currentValues: any[], valueExpr?: string): any[] {
    if (this.suppressAutoSelection) {
      return Array.isArray(currentValues) ? currentValues : [];
    }

    const normalizedCurrent = Array.isArray(currentValues) ? currentValues : [];
    if (!Array.isArray(items) || items.length !== 1) {
      return normalizedCurrent;
    }

    const singleValue = this.extractItemValue(items[0], valueExpr);
    if (singleValue == null) {
      return normalizedCurrent;
    }

    if (normalizedCurrent.length === 1 && normalizedCurrent[0] === singleValue) {
      return normalizedCurrent;
    }

    return [singleValue];
  }

  private patchDefaultTasksEditorOptions(items: any[]): void {
    const currentItems = Array.isArray(this.defaultTasksEditorOptions.items)
      ? this.defaultTasksEditorOptions.items
      : [];

    if (
      currentItems.length === items.length &&
      currentItems.every((item: any, index: number) =>
        item?.taskId === items[index]?.taskId && item?.taskName === items[index]?.taskName
      )
    ) {
      return;
    }

    this.defaultTasksEditorOptions.items = items;
  }

  private syncDefaultTasksSelection(selectedTaskIds: any[]): void {
    const normalizedTaskIds = Array.isArray(selectedTaskIds)
      ? [...selectedTaskIds]
      : [];

    this.selectedDefaultTasks = normalizedTaskIds;
    this.isTaskTypesSelected = normalizedTaskIds.length > 0;
    (this.fandCRequestData as any).taskTypes = normalizedTaskIds;
  }

  private arePrimitiveArraysEqual(left: any[], right: any[]): boolean {
    if (!Array.isArray(left) || !Array.isArray(right)) {
      return false;
    }

    if (left.length !== right.length) {
      return false;
    }

    for (let index = 0; index < left.length; index += 1) {
      if (left[index] !== right[index]) {
        return false;
      }
    }

    return true;
  }

  private isValueInItems(items: any[], value: any, valueExpr?: string): boolean {
    if (!Array.isArray(items) || items.length === 0 || value == null || value === '') {
      return false;
    }

    return items.some((item: any) => this.extractItemValue(item, valueExpr) === value);
  }

  private extractItemValue(item: any, valueExpr?: string): any {
    if (item == null) {
      return null;
    }

    if (!valueExpr) {
      return item;
    }

    return item?.[valueExpr] ?? null;
  }

  private syncEntityNameOptionsWithSelectedLoan(preferDerivedSelection: boolean): void {
    const selectedReportEntity =
      this.selectedReportEntityValue ??
      (this.fandCRequestData as any)?.reportEntity ??
      this.reportEntityOptions?.value ??
      null;

    const currentItems = Array.isArray(this.customEntityNameOptions?.items)
      ? this.customEntityNameOptions.items
      : [];
    const currentValue = this.customEntityNameOptions?.value ?? (this.fandCRequestData as any)?.entityName ?? null;
    const derivedItems = this.getDerivedEntityNameOptions(selectedReportEntity);

    // When the user has changed the report entity and there are no derived items for
    // the new entity type, clear the entity name rather than retaining stale data.
    if (preferDerivedSelection && derivedItems.length === 0) {
      this.entityNameOptions = { ...this.entityNameOptions, items: [], value: null };
      this.customEntityNameOptions = { ...this.customEntityNameOptions, items: [], value: null };
      this.fandCRequestData = { ...(this.fandCRequestData || {}), entityName: null } as any;
      if (currentValue != null) {
        this.store.dispatch(setEntityName({ selectedEntityName: null }));
      }
      return;
    }

    if (derivedItems.length === 0 && currentItems.length === 0) {
      return;
    }

    const mergedItems = this.mergeEntityNameOptions(currentItems, derivedItems, currentValue);
    const nextValue = preferDerivedSelection && derivedItems.length > 0
      ? derivedItems[0].label
      : this.getAutoSelectedValue(mergedItems, currentValue, 'label');

    this.entityNameOptions = {
      ...this.entityNameOptions,
      items: mergedItems,
      value: nextValue,
    };

    this.customEntityNameOptions = {
      ...this.customEntityNameOptions,
      items: mergedItems,
      value: nextValue,
    };

    this.fandCRequestData = {
      ...(this.fandCRequestData || {}),
      entityName: nextValue,
    } as any;

    if (nextValue !== currentValue) {
      this.store.dispatch(setEntityName({ selectedEntityName: nextValue }));
    }
  }

  private getDerivedEntityNameOptions(selectedReportEntity: string | null | undefined): { label: string; value: string }[] {
    const normalizedEntity = this.normalizeReportEntityValue(selectedReportEntity);
    if (!normalizedEntity) {
      return [];
    }

    const selectedLoan = this.resolveSelectedLoanForEntityPrefill();
    if (!selectedLoan) {
      return [];
    }

    let sourceValue: any = null;

    if (normalizedEntity === 'borrowers') {
      sourceValue =
        selectedLoan?.borrower ??
        selectedLoan?.borrowers ??
        selectedLoan?.facilityDetail?.borrower ??
        selectedLoan?.facilityDetail?.borrowers ??
        null;
    } else if (normalizedEntity === 'lenders') {
      sourceValue =
        selectedLoan?.investors ??
        selectedLoan?.lenders ??
        selectedLoan?.facilityDetail?.investors ??
        selectedLoan?.facilityDetail?.lenders ??
        null;
    }

    if (sourceValue == null) {
      const selectedLoanName =
        selectedLoan?.name ??
        selectedLoan?.Name ??
        selectedLoan?.facilityDetail?.name ??
        selectedLoan?.facilityDetail?.Name ??
        null;

      if (selectedLoanName && Array.isArray(this.availableLoans)) {
        const normalizedSelectedLoanName = selectedLoanName.toString().trim().toLowerCase();
        const matchingLoan = this.availableLoans.find((loan: any) => {
          const candidateValues = [loan?.name, loan?.Name, loan?.loanNumber, loan?.LoanNumber];
          return candidateValues
            .filter((value) => value != null)
            .some((value) => value.toString().trim().toLowerCase() === normalizedSelectedLoanName);
        });

        if (matchingLoan) {
          if (normalizedEntity === 'borrowers') {
            sourceValue = matchingLoan?.borrower ?? matchingLoan?.borrowers ?? null;
          } else if (normalizedEntity === 'lenders') {
            sourceValue = matchingLoan?.investors ?? matchingLoan?.lenders ?? null;
          }
        }
      }
    }

    return this.normalizeEntityNameValues(sourceValue).map((value) => ({
      label: value,
      value,
    }));
  }

  private normalizeEntityNameValues(sourceValue: any): string[] {
    const values = Array.isArray(sourceValue) ? sourceValue : [sourceValue];
    const normalizedValues = values
      .flatMap((item: any) => {
        const rawValue =
          typeof item === 'string'
            ? item
            : item?.name ?? item?.Name ?? item?.label ?? item?.Label ?? item?.value ?? item?.Value ?? null;

        if (rawValue == null) {
          return [];
        }

        const rawText = rawValue.toString().trim();
        if (!rawText) {
          return [];
        }

        // Treat legal entity suffixes as part of a single company name, not list delimiters.
        const legalSuffixPattern = /,\s*(llc|inc\.?|corp\.?|corporation|ltd\.?|lp|llp|pllc)\s*$/i;
        if (legalSuffixPattern.test(rawText)) {
          return [rawText];
        }

        return rawText
          .split(',')
          .map((value: string) => value.trim())
          .filter((value: string) => value.length > 0);
      });

    return Array.from(new Set(normalizedValues));
  }

  private resolveSelectedLoanForEntityPrefill(): any | null {
    const selectedLoan = this.selectedLoanOption ?? this.selectedLoanDetailSnapshot;
    if (selectedLoan && typeof selectedLoan === 'object') {
      const hasDirectEntityData =
        selectedLoan?.borrower != null ||
        selectedLoan?.borrowers != null ||
        selectedLoan?.investors != null ||
        selectedLoan?.lenders != null;

      if (hasDirectEntityData) {
        return selectedLoan;
      }

      const selectedLoanNameFromObject =
        selectedLoan?.name ??
        selectedLoan?.Name ??
        selectedLoan?.facilityDetail?.name ??
        selectedLoan?.facilityDetail?.Name ??
        null;

      if (selectedLoanNameFromObject && Array.isArray(this.availableLoans)) {
        const normalizedSelectedLoanName = selectedLoanNameFromObject.toString().trim().toLowerCase();
        const matchingLoan = this.availableLoans.find((loan: any) => {
          const candidateValues = [loan?.name, loan?.Name, loan?.loanNumber, loan?.LoanNumber];
          return candidateValues
            .filter((value) => value != null)
            .some((value) => value.toString().trim().toLowerCase() === normalizedSelectedLoanName);
        });

        if (matchingLoan) {
          return matchingLoan;
        }
      }

      return selectedLoan;
    }

    const formLoanValue = (this.fandCRequestData as any)?.loanNumber ?? selectedLoan ?? null;
    const selectedLoanName =
      typeof formLoanValue === 'object'
        ? (
            formLoanValue?.name ??
            formLoanValue?.Name ??
            formLoanValue?.loanNumber ??
            formLoanValue?.LoanNumber ??
            null
          )
        : formLoanValue;

    if (!selectedLoanName || !Array.isArray(this.availableLoans)) {
      return this.selectedLoanDetailSnapshot ?? null;
    }

    const normalizedSelectedLoanName = selectedLoanName.toString().trim().toLowerCase();
    return (
      this.availableLoans.find((loan: any) => {
        const candidateValues = [
          loan?.name,
          loan?.Name,
          loan?.loanNumber,
          loan?.LoanNumber,
          loan?.referenceName,
          loan?.ReferenceName,
        ];

        return candidateValues
          .filter((value) => value != null)
          .some((value) => value.toString().trim().toLowerCase() === normalizedSelectedLoanName);
      }) ??
      this.selectedLoanDetailSnapshot ??
      null
    );
  }

  private normalizeReportEntityValue(reportEntity: string | null | undefined): string {
    const value = (reportEntity ?? '').toString().trim().toLowerCase();
    const entityMap: Record<string, string> = {
      borrower: 'borrowers',
      borrowers: 'borrowers',
      lender: 'lenders',
      lenders: 'lenders',
    };

    return entityMap[value] ?? value;
  }

  private mergeEntityNameOptions(
    existingItems: { label: string; value: string }[],
    derivedItems: { label: string; value: string }[],
    currentValue: string | null | undefined
  ): { label: string; value: string }[] {
    const mergedMap = new Map<string, { label: string; value: string }>();

    [...derivedItems, ...existingItems].forEach((item: any) => {
      const label = (item?.label ?? item?.value ?? '').toString().trim();
      if (!label) {
        return;
      }

      if (!mergedMap.has(label)) {
        mergedMap.set(label, {
          label,
          value: (item?.value ?? label).toString(),
        });
      }
    });

    const normalizedCurrentValue = (currentValue ?? '').toString().trim();
    if (normalizedCurrentValue && !mergedMap.has(normalizedCurrentValue)) {
      mergedMap.set(normalizedCurrentValue, {
        label: normalizedCurrentValue,
        value: normalizedCurrentValue,
      });
    }

    return Array.from(mergedMap.values());
  }

  onFormDataChanged(event: any): void {
    // console.log(event);
    // Handle form data changes here if needed
    if (!event) {
      return;
    }

    const isDateUserInitiatedChange = !!event.event || !!event.component;

    if (event.dataField === 'referenceDate') {
      if (!isDateUserInitiatedChange) {
        return;
      }

      if (event.value == null) {
        return;
      }

      const previousReferenceDate = this.referenceDateOptions?.value;
      const previousReferenceTime = previousReferenceDate ? new Date(previousReferenceDate as any).getTime() : NaN;
      const nextReferenceTime = new Date(event.value as any).getTime();
      if (!isNaN(previousReferenceTime) && previousReferenceTime === nextReferenceTime) {
        return;
      }

      this.store.dispatch(
        setReferenceDate({ selectedReferenceDate: event.value })
      );
      return;
    }

    if (event.dataField === 'maturityDate') {
      if (!isDateUserInitiatedChange) {
        return;
      }

      if (event.value == null) {
        return;
      }

      const previousMaturityDate = this.maturityDateOptions?.value;
      const previousMaturityTime = previousMaturityDate ? new Date(previousMaturityDate as any).getTime() : NaN;
      const nextMaturityTime = new Date(event.value as any).getTime();
      if (!isNaN(previousMaturityTime) && previousMaturityTime === nextMaturityTime) {
        return;
      }

      this.store.dispatch(
        setMaturityDate({ selectedMaturityDate: event.value })
      );
      return;
    }

    // For non-date fields, only handle explicit user events to avoid dispatch loops
    // from programmatic DevExtreme form updates.
    if (!event.event) {
      return;
    }

    if (event.dataField === 'loanNumber') {
      const selectedItem = event.component?.option('selectedItem');
      const normalizedValue = (event.value ?? '').toString().trim().toLowerCase();
      const resolvedLoan = this.availableLoans.find((loan: any) => {
        const candidateValues = [
          loan?.name,
          loan?.Name,
          loan?.loanNumber,
          loan?.LoanNumber,
          loan?.referenceName,
          loan?.ReferenceName,
        ];

        return candidateValues
          .filter((value) => value != null)
          .some((value) => value.toString().trim().toLowerCase() === normalizedValue);
      });

      const selectedLoan = selectedItem ?? resolvedLoan ?? event.value;
      this.selectedLoanOption = selectedLoan;

      const selectedLoanId =
        selectedLoan?.id ??
        selectedLoan?.Id ??
        selectedLoan?.facilityId ??
        selectedLoan?.FacilityId ??
        resolvedLoan?.id ??
        resolvedLoan?.Id ??
        resolvedLoan?.facilityId ??
        resolvedLoan?.FacilityId ??
        null;

      this.store.dispatch(setLoanNumber({ selectedLoan: selectedLoan }));
      this.syncEntityNameOptionsWithSelectedLoan(true);
      if (selectedLoanId) {
        this.store.dispatch(fetchLoanDetail({ facilityId: selectedLoanId }));
      }
      // console.log('loanNumber value:', event.value);
      this.isLoanNumberPopulated = !!selectedLoan;
    }
  }

  onShowInactiveLoansChanged = (e: any) => {
    this.showInactiveLoans = e.value;

    // Defer the BehaviorSubject update to avoid ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      this.showInactiveLoansSubject.next(e.value);
    }, 0);
  }

  onPrototypeRowPrepared = (e: any) => {
    if (e?.rowType !== 'data' || !e?.data || !e?.rowElement) {
      return;
    }

    const rowId = e.data.rowId;
    if (rowId && this.highlightedPrototypeRowIds.has(rowId)) {
      e.rowElement.classList.add('duplicate-row-highlight');
    } else {
      e.rowElement.classList.remove('duplicate-row-highlight');
    }
  }

  private highlightRows(rowIds: string[]): void {
    this.highlightedPrototypeRowIds = new Set(rowIds.filter((x) => !!x));
    this.prototypeTasksGrid?.instance?.refresh();

    if (this.highlightedRowsClearTimer) {
      clearTimeout(this.highlightedRowsClearTimer);
    }

    this.highlightedRowsClearTimer = setTimeout(() => {
      this.clearHighlightedRows();
    }, 4000);
  }

  private clearHighlightedRows(): void {
    if (this.highlightedPrototypeRowIds.size === 0) {
      return;
    }

    this.highlightedPrototypeRowIds.clear();
    this.prototypeTasksGrid?.instance?.refresh();
  }

  private clearEditModeInputs(): void {
    this.selectedTemplateType = null;
    this.selectedTemplate = null;
    this.selectedDefaultTasks = [];
    this.isTaskTypesSelected = false;
    this.selectedFrequency = null;
    this.selectedFrequencyOffsetValue = null;
    this.selectedFormatValue = null;
    this.selectedReportEntityValue = null;
    this.selectedEntityName = null;

    this.templateTypeEditorOptions = { ...this.templateTypeEditorOptions, value: null };
    this.templateEditorOptions = { ...this.templateEditorOptions, value: null };
    this.frequencyEditorOptions = { ...this.frequencyEditorOptions, value: null };
    this.frequencyOffsetEditorOptions = { ...this.frequencyOffsetEditorOptions, value: null };
    this.oneTimeDueDateOptions = { ...this.oneTimeDueDateOptions, value: null };
    this.formatEditorOptions = { ...this.formatEditorOptions, value: null };
    this.reportEntityOptions = { ...this.reportEntityOptions, value: null };
    this.customEntityNameOptions = { ...this.customEntityNameOptions, value: null };
    this.referenceDateOptions = { ...this.referenceDateOptions, value: null };

    this.fandCRequestData = {
      ...(this.fandCRequestData || {}),
      category: null,
      frequency: null,
      frequencyOffsetOption: null,
      oneTimeDueDate: null,
      format: null,
      reportEntity: null,
      entityName: null,
      referenceDate: null,
      taskTypes: [],
    } as any;
  }

  onExporting(e) {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Client Compliance Report');

    exportDataGrid({
      component: e.component,
      worksheet: worksheet,
      autoFilterEnabled: true,
      topLeftCell: { row: 2, column: 1 },
                  customizeCell: ({ gridCell, excelCell }) => {
              if (gridCell.rowType === "header") {
                // Dark grey header background with white text
                excelCell.fill = {
                  type: "pattern",
                  pattern: "solid",
                  fgColor: { argb: "FF4F4F4F" }, // Dark grey
                };
                excelCell.font = { bold: true, color: { argb: "FFFFFFFF" } }; // White text
                excelCell.alignment = {
                  horizontal: "center",
                  vertical: "middle",
                };
              }
              if (gridCell.rowType === "data") {
                // Check if this is a date column that needs special formatting
                if (gridCell.column &&
                  (gridCell.column.dataField === 'referenceDate' ||
                   gridCell.column.dataField === 'firstDueDate')) {
                  this.setDateCell(gridCell, excelCell);
                }
                // Alternate row shading for data rows
                if (excelCell.row % 2 === 0) {
                  // Light grey for even rows
                  excelCell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "FFEFEFEF" }, // Light grey
                  };
                } else {
                  // White for odd rows (default)
                  excelCell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "FFFFFFFF" }, // White
                  };
                }
                excelCell.alignment = {
                  horizontal: "left",
                  vertical: "middle",
                };
              }
            },

    }).then((cellRange) => {
      // header
      const headerRow = worksheet.getRow(1);
      headerRow.height = 30;
      worksheet.mergeCells(1, 1, 1, 8);

      headerRow.getCell(1).value = 'Client Compliance Report';
      headerRow.getCell(1).font = { name: 'Segoe UI Light', size: 22 };
      headerRow.getCell(1).alignment = { horizontal: 'center' };

      // footer
      const footerRowIndex = cellRange.to.row + 2;
      const footerRow = worksheet.getRow(footerRowIndex);
      worksheet.mergeCells(footerRowIndex, 1, footerRowIndex, 8);

      footerRow.getCell(1).value = 'CreditHub TaskManager - MountStreet ';
      footerRow.getCell(1).font = { color: { argb: 'BFBFBF' }, italic: true };
      footerRow.getCell(1).alignment = { horizontal: 'right' };
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        const now = new Date();
        const formattedDate = now.getFullYear() + '_' +
          String(now.getMonth() + 1).padStart(2, '0') + '_' +
          String(now.getDate()).padStart(2, '0');

        saveAs(new Blob([buffer], { type: 'application/octet-stream' }),
          `Client Compliance Report_${this.loanDisplayText}_${formattedDate}.xlsx`);
      });
    });
    e.cancel = true;
    //this.loggingService.logApplicationEvent(APPLICATION_EVENTS.EXPORT_TO_EXCEL, { reportName: "property-overview" });
  }

  private setDateCell(gridCell, excelCell: any) {
    if (gridCell.value instanceof Date) {
      const utcDate = new Date(Date.UTC(
        gridCell.value.getFullYear(),
        gridCell.value.getMonth(),
        gridCell.value.getDate()
      ));
      excelCell.numFmt = "MMM DD YYYY"; // Excel date format
      excelCell.value = utcDate;
    }
    else if (typeof gridCell.value === "string") {
      // Try to parse the date string
      const parsedDate = new Date(gridCell.value);
      // Check if the parsed date is valid
      if (isNaN(parsedDate.getTime())) {
        excelCell.value = gridCell.value; // Keep original value if parsing fails
      } else {
        excelCell.value = parsedDate;
      }
      excelCell.numFmt = "DD MMM YYYY"; // Excel date format
      excelCell.value = parsedDate;
    } else {
      excelCell.value = gridCell.value;
    }
  }

  // Custom item template function for loan dropdown
  loanItemTemplate = (itemData: any, itemIndex: number, itemElement: any) => {
    const cssClass = itemData.status === 'Inactive' ? 'inactive-item' : 'active-item';
    const displayText = `${itemData.name} (${itemData.status})`;
    itemElement.innerHTML = DOMPurify.sanitize(`<div class="${cssClass}">${displayText}</div>`);
    return itemElement;
  }
}
